import smartDate from "@qrohlf/smart-date";
import cx from "classnames";
import { CoreLink } from "common/CoreLink";
import { Heart } from "common/Heart";
import { SegmentCard } from "common/hovercards/SegmentCard";
import { SegmentMatchCard } from "common/hovercards/SegmentMatchCard";
import { TRouteCard } from "common/hovercards/TRouteCard";
import { UserCard } from "common/hovercards/UserCard";
import { Icon } from "common/Icon";
import { ProfileImg } from "common/ProfileImg";
import { CalendarDate } from "common/styleguide/CalendarDate";
import { RichListItem, RichListItemBadge as Badge, RichListItemDetail as Detail } from "common/styleguide/RichListItem";
import styles from "common/styleguide/RichListItem/RichListItem.scss";
import { Distance, Duration, Elevation, Speed } from "common/units";
import humanizeDuration from "humanize-duration";
import { commonMetrics, commonNouns, commonTRouteMessages, commonVerbs, featureNames, fm, Msg } from "lib/localization";
import { j } from "lib/textUtils";
import { urls } from "lib/urls";
import { experimentEnabled } from "lib/utils";
import _ from "lodash";
import React, { Fragment } from "react";
import { defineMessages, FormattedMessage as T, injectIntl } from "react-intl";
import { HighlightedPhoto } from "./HighlightedPhoto";
import upgradeBasic from "./images/upgrade-graphic-basic.jpg";
import upgradeBasic2x from "./images/upgrade-graphic-basic@2x.jpg";
import upgradeBasic3x from "./images/upgrade-graphic-basic@3x.jpg";
import upgradePremium from "./images/upgrade-graphic-premium.jpg";
import upgradePremium2x from "./images/upgrade-graphic-premium@2x.jpg";
import upgradePremium3x from "./images/upgrade-graphic-premium@3x.jpg";

const pl = (str) => (qty) => qty === 1 ? str : str + "s";

const hasLocation = (obj) => obj.locality || obj.administrativeArea;

const formatLocation = (obj) => j([obj.locality, obj.administrativeArea]);

const shortHumanizer = humanizeDuration.humanizer({
  delimiter: " ",
  language: "shortEn",
  languages: {
    shortEn: {
      y: pl("yr"),
      mo: pl("mo"),
      w: pl("wk"),
      d: pl("day"),
      h: pl("hr"),
      m: () => "min",
      s: () => "sec",
      ms: () => "ms",
    },
  },
});

export const EventListItem = ({ event, includePhoto }) => (
  <RichListItem
    title={<a href={urls.events.show(event.id)}>{event.name}</a>}
    hoverable={false}
    left={event.startsOn ? <CalendarDate large date={event.startsOn} /> : null}
    right={
      includePhoto && <HighlightedPhoto photoId={event.highlightedPhotoId} parentType={"event"} parentId={event.id} />
    }
    footer={<Detail>by {event.userName}</Detail>}
  >
    {event.startsOn && <Detail>{smartDate(event.endsOn ? [event.startsOn, event.endsOn] : event.startsOn)}</Detail>}
    {event.location && <Detail>{event.location}</Detail>}
  </RichListItem>
);

const _TripListItem = ({
  trip,
  pinnedAt,
  editable,
  pinnable,
  deletable,
  pinned,
  pin,
  unpin,
  likable,
  like,
  unlike,
  deleteClicked,
  isFeedEntry,
  friend,
  source,
  noBadge,
  deleteTitle,
  draggable,
  dragHandleProps,
  hoverDisabled,
  intl,
}) => {
  const timestamp = (() => {
    if (isFeedEntry) {
      return `${fm(intl, commonVerbs.uploaded)} ${smartDate(trip.createdAt, {
        includeTime: false,
        relative: "week",
      })}`;
    }
    if (pinnedAt) {
      return `${fm(intl, sourceString(source))} ${smartDate(pinnedAt, {
        includeTime: false,
        relative: "week",
      })}`;
    }
    return smartDate(trip.departedAt, { includeTime: false, relative: "week" });
  })();
  const LinkComponent = (chunks) => (experimentEnabled("trsp_redesign") ? <CoreLink {...chunks} /> : <a {...chunks} />);
  const { likedByCurrentUser, likesCount } = trip;

  return (
    <RichListItem
      title={
        <LinkComponent
          to={urls.trips.show(trip.id, trip.privacyCode)}
          href={urls.trips.show(trip.id, trip.privacyCode)}
        >
          {trip.name}
        </LinkComponent>
      }
      hoverable={!hoverDisabled && (pinnable || editable || deletable)}
      right={
        <Fragment>
          <HighlightedPhoto
            photoId={trip.highlightedPhotoId}
            parentType={"trip"}
            parentId={trip.id}
            readonly={!editable}
            privacyCode={trip.privacyCode}
          />
          <TripThumb trip={trip} />
        </Fragment>
      }
      footer={
        <Fragment>
          {!noBadge && (
            <Detail>
              <Badge color="#FFF" bg="#38AA62">
                {_.capitalize(fm(intl, commonNouns.rides, { count: 1 }))}
              </Badge>
            </Detail>
          )}
          {friend && (
            <Detail hoverable>
              <UserCard id={friend.id} name={friend.name}>
                <a href={urls.users.show(friend.id)}>{friend.name}</a>
              </UserCard>
            </Detail>
          )}
          <Detail>{timestamp}</Detail>
          {likable && (
            <Detail className={styles.heartDetail}>
              <Heart
                size={20}
                onClick={() => (likedByCurrentUser ? unlike() : like())}
                liked={likedByCurrentUser}
                count={likesCount}
                emptyOnZero
              />
            </Detail>
          )}
          <Detail className={styles.showDetailOnHover}>
            {editable && (
              <a href={`/trips/${trip.id}/edit`}>
                <Icon
                  i="md-create"
                  size={15}
                  className={styles.controlIcon}
                  data-tip
                  title={fm(intl, commonVerbs.edit)}
                />
              </a>
            )}
            {pinnable && (
              <Icon
                onClick={() => (pinned ? unpin(trip.id) : pin(trip.id))}
                data-tip
                title={fm(intl, pinned ? commonVerbs.unpin : commonVerbs.pin)}
                i="pin"
                size={15}
                className={cx(styles.controlIcon, pinned && styles.pinned)}
              />
            )}
            {deletable && (
              <Icon
                onClick={deleteClicked}
                i="md-trash"
                size={15}
                className={styles.controlIcon}
                data-tip
                title={deleteTitle || fm(intl, commonVerbs.delete)}
              />
            )}
            {draggable && <Icon i="md-move" size={18} className={styles.controlIcon} {...dragHandleProps} />}
          </Detail>
        </Fragment>
      }
    >
      {(trip.locality || trip.administrativeArea) && (
        <Detail data-tip title={fm(intl, commonTRouteMessages.startLocation)}>
          {j([trip.locality, trip.administrativeArea])}
        </Detail>
      )}
      <Detail data-tip title={fm(intl, commonMetrics.distance)}>
        <Distance short value={trip.distance} />
      </Detail>
      <Detail data-tip title={_.startCase(fm(intl, commonMetrics.elevationGain))}>
        <Elevation short value={trip.elevationGain} />
      </Detail>
      <Detail data-tip title={_.startCase(fm(intl, commonMetrics.movingTime))}>
        {shortHumanizer(trip.movingTime * 1000, {
          units: ["h", "m"],
          round: true,
        })}
      </Detail>
    </RichListItem>
  );
};
export const TripListItem = injectIntl(_TripListItem);

const rliMessage = defineMessages({
  planned: {
    id: "RouteListItem.planned",
    defaultMessage: "planned",
  },
});

const _RouteListItem = ({
  route,
  pinnedAt,
  editable,
  pinnable,
  deletable,
  pinned,
  pin,
  unpin,
  likable,
  like,
  unlike,
  deleteClicked,
  isFeedEntry,
  friend,
  source,
  noBadge,
  deleteTitle,
  draggable,
  dragHandleProps,
  hoverDisabled,
  intl,
}) => {
  const timestamp = (() => {
    if (isFeedEntry) {
      return `${fm(intl, rliMessage.planned)} ${smartDate(route.createdAt, {
        includeTime: false,
        relative: "week",
      })}`;
    }
    if (pinnedAt) {
      return `${fm(intl, sourceString(source))} ${smartDate(pinnedAt, {
        includeTime: false,
        relative: "week",
      })}`;
    }
    return smartDate(route.createdAt, { includeTime: false, relative: "week" });
  })();
  const { likedByCurrentUser, likesCount } = route;

  return (
    <RichListItem
      title={<CoreLink to={urls.routes.show(route.id, route.privacyCode)}>{route.name}</CoreLink>}
      hoverable={!hoverDisabled && (pinnable || editable || deletable)}
      right={
        <Fragment>
          <HighlightedPhoto
            photoId={route.highlightedPhotoId}
            parentType={"route"}
            parentId={route.id}
            readonly={!editable}
            privacyCode={route.privacyCode}
          />
          <RouteThumb route={route} />
        </Fragment>
      }
      footer={
        <Fragment>
          {!noBadge && (
            <Detail>
              <Badge color="#FFF" bg="#29B6BD">
                <Msg capitalized m={commonNouns.routes} values={{ count: 1 }} />
              </Badge>
            </Detail>
          )}
          {friend && (
            <Detail hoverable>
              <UserCard id={friend.id} name={friend.name}>
                <a href={urls.users.show(friend.id)}>{friend.name}</a>
              </UserCard>
            </Detail>
          )}
          <Detail>{timestamp}</Detail>
          {likable && (
            <Detail className={styles.heartDetail}>
              <Heart
                size={20}
                onClick={() => (likedByCurrentUser ? unlike() : like())}
                liked={likedByCurrentUser}
                count={likesCount}
                emptyOnZero
              />
            </Detail>
          )}
          <Detail className={styles.showDetailOnHover}>
            {editable && (
              <a href={`/routes/${route.id}/edit`}>
                <Icon
                  i="md-create"
                  size={15}
                  className={styles.controlIcon}
                  data-tip
                  title={fm(intl, commonVerbs.edit)}
                />
              </a>
            )}
            {pinnable && (
              <Icon
                onClick={() => (pinned ? unpin(route.id) : pin(route.id))}
                data-tip
                title={fm(intl, pinned ? commonVerbs.unpin : commonVerbs.pin)}
                i="pin"
                size={15}
                className={cx(styles.controlIcon, pinned && styles.pinned)}
              />
            )}
            {deletable && (
              <Icon
                onClick={deleteClicked}
                i="md-trash"
                size={15}
                className={styles.controlIcon}
                data-tip
                title={deleteTitle || fm(intl, commonVerbs.delete)}
              />
            )}
            {draggable && <Icon i="md-move" size={18} className={styles.controlIcon} {...dragHandleProps} />}
          </Detail>
        </Fragment>
      }
    >
      <Detail data-tip title={fm(intl, commonTRouteMessages.startLocation)}>
        {j([route.locality, route.administrativeArea])}
      </Detail>
      <Detail data-tip title={fm(intl, commonMetrics.distance)}>
        <Distance short value={route.distance} />
      </Detail>
      <Detail data-tip title={_.startCase(fm(intl, commonMetrics.elevationGain))}>
        <Elevation short value={route.elevationGain} />
      </Detail>
      {route.hasCoursePoints && (
        <Detail data-tip title={fm(intl, commonTRouteMessages.hasCuesheet)}>
          <Icon i="md-document" />
        </Detail>
      )}
    </RichListItem>
  );
};

export const RouteListItem = injectIntl(_RouteListItem);

const sourceString = (source) => {
  switch (source) {
    case "s2d": {
      return commonVerbs.sentToDevice;
    }
    case "download": {
      return commonVerbs.downloaded;
    }
    default: {
      return commonVerbs.pinned;
    }
  }
};

const subscriptionListItemMessages = defineMessages({
  basic: {
    id: "subscriptionListItemMessages.basic",
    defaultMessage: "Upgraded to Basic",
  },
  premium: {
    id: "subscriptionListItemMessages.premium",
    defaultMessage: "Upgraded to Premium",
  },
});

const _SubscriptionListItem = ({ subscription, currentUser, friend, intl }) => {
  const premium = subscription.accountLevel === 2;
  const higher = subscription.accountLevel > currentUser.accountLevel;
  return (
    <RichListItem
      title={fm(intl, premium ? subscriptionListItemMessages.premium : subscriptionListItemMessages.basic)}
      right={<UpgradeImage premium={premium} />}
      footer={
        <Fragment>
          {friend && (
            <Detail hoverable>
              <UserCard id={friend.id} name={friend.name}>
                <a href={urls.users.show(friend.id)}>{friend.name}</a>
              </UserCard>
            </Detail>
          )}
          <Detail>
            {smartDate(subscription.updatedAt, {
              includeTime: false,
              relative: "week",
            })}
          </Detail>
        </Fragment>
      }
    >
      {higher ? (
        <a href="/choose_account">
          <T id="listItems.seePlanOptions" defaultMessage="See Plan Options" />
        </a>
      ) : (
        <div className={styles.padder} />
      )}
    </RichListItem>
  );
};

export const SubscriptionListItem = injectIntl(_SubscriptionListItem);

const UpgradeImage = ({ premium }) => (
  <Fragment>
    {premium ? (
      <img src={upgradePremium} srcSet={`${upgradePremium} 1x, ${upgradePremium2x} 2x, ${upgradePremium3x} 3x`} />
    ) : (
      <img src={upgradeBasic} srcSet={`${upgradeBasic} 1x, ${upgradeBasic2x} 2x, ${upgradeBasic3x} 3x`} />
    )}
  </Fragment>
);

const _GearListItem = ({ gear, pinnedAt, isFeedEntry, friend, intl }) => {
  const timestamp = (() => {
    if (isFeedEntry) {
      return `${fm(intl, commonVerbs.added)} ${smartDate(gear.createdAt, {
        includeTime: false,
        relative: "week",
      })}`;
    }
    if (pinnedAt) {
      return `${fm(intl, commonVerbs.pinned)} ${smartDate(pinnedAt, {
        includeTime: false,
        relative: "week",
      })}`;
    }
    return smartDate(gear.createdAt, { includeTime: false, relative: "week" });
  })();

  return (
    <RichListItem
      title={<a href={urls.gear.show(gear.id)}>{gear.name}</a>}
      right={
        <Fragment>
          {gear.highlightedPhotoId > 0 && (
            <img
              style={{ display: "inline-block", height: "80px", width: "80px" }}
              src={urls.photos.profile(gear.highlightedPhotoId)}
            />
          )}
        </Fragment>
      }
      footer={
        <Fragment>
          <Detail>
            <Badge color="#212121" bg="#F0DCC8">
              <Msg m={featureNames.gear} />
            </Badge>
          </Detail>
          {friend && (
            <Detail hoverable>
              <UserCard id={friend.id} name={friend.name}>
                <a href={urls.users.show(friend.id)}>{friend.name}</a>
              </UserCard>
            </Detail>
          )}
          <Detail>{timestamp}</Detail>
        </Fragment>
      }
    >
      {gear.description.length >= 50 ? `${gear.description.substring(0, 44)}. . .` : gear.description}
    </RichListItem>
  );
};

export const GearListItem = injectIntl(_GearListItem);

const _SegmentMatchListItem = ({ match, intl }) => (
  <RichListItem
    title={<a href={`/segments/${match.slug}`}>{match.name}</a>}
    right={<SegmentMatchThumb large match={match} style={{ display: "inline-block", height: "80px", width: "80px" }} />}
    footer={
      match.rank && (
        <Badge color="#424242" bg="#C8E6DC">
          <T id="SegmentMatchListItem.rank" defaultMessage="Rank" /> #{match.rank}
        </Badge>
      )
    }
  >
    <Detail data-tip title={fm(intl, commonMetrics.distance)}>
      <Distance short bigUnits value={match.metrics.distance} />
    </Detail>
    <Detail data-tip title={_.startCase(fm(intl, commonMetrics.elevationGain))}>
      <Elevation short value={match.metrics.eleGain} />
    </Detail>
    <Detail data-tip title={_.startCase(fm(intl, commonMetrics.movingTime))}>
      <Duration short biggestUnits valueOnly value={match.metrics.duration} />
    </Detail>
    <Detail data-tip title={_.startCase(fm(intl, commonMetrics.speed))}>
      <Speed short value={match.metrics.speed.avg} />
    </Detail>
    <Detail icon="md-ribbon" iconColor="#FA6400">
      {match.efforts ?? 0}{" "}
      <T
        id="SegmentMatchListItem.effort"
        defaultMessage="{efforts, plural, one {effort} other {efforts}}"
        values={{ efforts: match.efforts }}
      />
    </Detail>
  </RichListItem>
);

export const SegmentMatchListItem = injectIntl(_SegmentMatchListItem);

const _SegmentListItem = ({ segment, pinnedAt, pinnable, pinned, pin, unpin, intl }) => (
  <RichListItem
    title={<a href={`/segments/${segment.slug}`}>{segment.title}</a>}
    hoverable={pinnable}
    right={<SegmentThumb large segment={segment} style={{ display: "inline-block", height: "80px", width: "80px" }} />}
    footer={
      <Fragment>
        <Detail>
          <Badge color="#424242" bg="#C8E6DC">
            <Msg capitalized m={commonNouns.segments} values={{ count: 1 }} />
          </Badge>
        </Detail>
        {pinnedAt && (
          <Detail>
            {fm(intl, commonVerbs.pinned)} {smartDate(pinnedAt, { includeTime: false, relative: "week" })}
          </Detail>
        )}
        <Detail className={styles.showDetailOnHover}>
          {pinnable && (
            <Icon
              onClick={() => (pinned ? unpin(segment.id) : pin(segment.id))}
              data-tip
              title={fm(intl, pinned ? commonVerbs.unpin : commonVerbs.pin)}
              i="pin"
              size={15}
              className={cx(styles.controlIcon, pinned && styles.pinned)}
            />
          )}
        </Detail>
      </Fragment>
    }
  >
    <Detail data-tip title={fm(intl, commonMetrics.distance)}>
      <Distance short bigUnits value={segment.distance} />
    </Detail>
    <Detail data-tip title={_.startCase(fm(intl, commonMetrics.elevationGain))}>
      <Elevation short value={segment.eleGain} />
    </Detail>
  </RichListItem>
);

export const SegmentListItem = injectIntl(_SegmentListItem);

const _UserListItem = ({ user, pinnedAt, pinnable, pinned, pin, unpin, intl }) => (
  <RichListItem
    title={<a href={urls.users.show(user.id)}>{user.name}</a>}
    hoverable={pinnable}
    right={
      <Fragment>
        <ProfileImg large user={user} style={{ display: "inline-block", height: "80px", width: "80px" }} />
      </Fragment>
    }
    footer={
      <Fragment>
        <Detail>
          <Badge color="#FFF" bg="#B950A0">
            <Msg capitalized m={commonNouns.user} />
          </Badge>
        </Detail>
        {pinnedAt && (
          <Detail>
            {fm(intl, commonVerbs.pinned)} ${smartDate(pinnedAt, { includeTime: false, relative: "week" })}
          </Detail>
        )}
        <Detail className={styles.showDetailOnHover}>
          {pinnable && (
            <Icon
              onClick={() => (pinned ? unpin(user.id) : pin(user.id))}
              data-tip
              title={"Click to " + (pinned ? "unpin" : "pin")}
              i="pin"
              size={15}
              className={cx(styles.controlIcon, pinned && styles.pinned)}
            />
          )}
        </Detail>
      </Fragment>
    }
  >
    <Detail data-tip title="Start Location">
      {j([user.locality, user.administrativeArea])}
    </Detail>
  </RichListItem>
);

export const UserListItem = injectIntl(_UserListItem);

export class OrgListItem extends React.PureComponent {
  render() {
    const { org } = this.props;
    return (
      <RichListItem
        title={<a href={urls.organizations.show(org.slug)}>{org.name}</a>}
        left={
          <a href={urls.organizations.show(org.slug)}>
            <img src={org.logo} height="70" width="70" />
          </a>
        }
        footer={
          <Fragment>
            {hasLocation(org) && <Detail>{formatLocation(org)}</Detail>}
            {hasLocation(org) && org.subhead && <br />}
            {org.subhead && <Detail>{org.subhead}</Detail>}
          </Fragment>
        }
      />
    );
  }
}

export class TripThumb extends React.PureComponent {
  static defaultProps = {
    size: 80,
  };

  render() {
    const { trip, size } = this.props;
    return (
      <a href={urls.trips.show(trip.id)} style={{ display: "inline-block" }}>
        <TRouteCard
          type="trip"
          id={trip.id}
          name={trip.name}
          distance={trip.distance}
          elevationGain={trip.elevationGain}
          elevationLoss={trip.elevationLoss}
          movingTime={trip.movingTime}
        >
          <img
            src={urls.staticMap("trip", "thumb", trip.id)}
            style={{ width: size, height: size, objectFit: "cover" }}
          />
        </TRouteCard>
      </a>
    );
  }
}

export class RouteThumb extends React.PureComponent {
  static defaultProps = {
    size: 80,
  };

  render() {
    const { route, size } = this.props;
    return (
      <CoreLink href={urls.routes.show(route.id, route.privacyCode)} style={{ display: "inline-block" }}>
        <TRouteCard
          type="route"
          id={route.id}
          name={route.name}
          distance={route.distance}
          elevationGain={route.elevationGain}
          elevationLoss={route.elevationLoss}
        >
          <img
            src={urls.staticMap("route", "thumb", route.id)}
            style={{ width: size, height: size, objectFit: "cover" }}
          />
        </TRouteCard>
      </CoreLink>
    );
  }
}

export class SegmentThumb extends React.PureComponent {
  static defaultProps = {
    size: 80,
  };

  render() {
    const { segment, size } = this.props;
    return (
      <a href={urls.segments.show(segment.slug)} style={{ display: "inline-block" }}>
        <SegmentCard
          type="segment"
          id={segment.id}
          title={segment.title}
          distance={segment.distance}
          elevationGain={segment.eleGain}
          locality={segment.locality}
          administrativeArea={segment.administrativeArea}
        >
          <img
            src={urls.staticMap("segment", "thumb", segment.id)}
            style={{ width: size, height: size, objectFit: "cover" }}
          />
        </SegmentCard>
      </a>
    );
  }
}

export class SegmentMatchThumb extends React.PureComponent {
  static defaultProps = {
    size: 80,
  };

  render() {
    const { match, size } = this.props;
    return (
      <a href={urls.segments.show(match.slug)} style={{ display: "inline-block" }}>
        <SegmentMatchCard
          type="segment"
          id={match.segmentId}
          title={match.name}
          distance={match.metrics.distance}
          elevationGain={match.metrics.eleGain}
          avgSpeed={match.metrics.speed.avg}
        >
          <img
            src={urls.staticMap("segment", "thumb", match.segmentId)}
            style={{ width: size, height: size, objectFit: "cover" }}
          />
        </SegmentMatchCard>
      </a>
    );
  }
}
